import { type Nullable } from '../types';

/**
 * Describes the source of an interaction.
 *
 * Useful for analytics, undo/redo grouping, and deciding whether to
 * broadcast changes to other users (e.g., usually don't broadcast 'presence').
 */
export type InteractionSource = 'mouse' | 'keyboard' | 'api' | 'presence';

/**
 * The current selection mode. There will never be any selections in the state when in 'idle'.
 */
export type SelectionMode = 'idle' | 'cell' | 'row' | 'column';

/**
 * A single coordinate along one axis (Row or Column).
 *
 * Uses index-based computation for all selection operations.
 */
export interface GridCoord {
  /**
   * The current 0-based visual index on the screen.
   */
  index: number;
}

/**
 * A precise 2D location on the grid.
 *
 * Represents the intersection of a specific Row and a specific Column.
 * Note: Even for full-row selections, we use this structure (ignoring the col part).
 */
export interface GridPosition {
  /**
   * The vertical coordinate (The Row).
   */
  row: GridCoord;

  /**
   * The horizontal coordinate (The Column).
   */
  col: GridCoord;
}

/**
 * Represents a single continuous highlight box on the grid.
 *
 * A user can create multiple `SelectionRange` objects by holding Cmd/Ctrl
 * and clicking different areas. This is the "O(1)" scalable way to store
 * selections, rather than storing every selected cell individually.
 */
export interface SelectionRange {
  /**
   * A unique identifier for this specific highlight instance (e.g., UUIDv4).
   *
   * CRITICAL FOR PERFORMANCE:
   * This allows the renderer to update *only* this box when dragging,
   * without re-rendering or diffing the entire selection list.
   */
  id: string;

  /**
   * The "Anchor" Point.
   * This is where the user *started* the selection (MouseDown).
   * It remains stationary while the user drags.
   */
  anchor: GridPosition;

  /**
   * The "Lead" Point.
   * This is where the user's interaction *currently is* (Mouse position).
   * This point moves as the user drags or uses Shift+Arrow keys.
   */
  lead: GridPosition;
}

/**
 * A discriminated union representing the current selection with its values.
 * The `mode` field acts as the discriminant.
 */
export type FlattenedSelection =
  | { mode: 'idle'; values: null }
  | { mode: 'cell'; values: { row: number; col: number }[] }
  | { mode: 'row'; values: number[] | 'all' }
  | { mode: 'column'; values: number[] };

/**
 * The complete snapshot of the selection engine state.
 */
export interface SelectionState {
  /**
   * For external items, like the action bar, and to support other systems
   * we only allow a single mode selection at a time. So, you may only be
   * selection cells, columns, rows, etc. This is the current mode in which
   * a user is selecting.
   */
  mode: SelectionMode;

  /**
   * The stack of all active highlights.
   *
   * - A standard click clears this array and pushes 1 new range.
   * - A Cmd+Click pushes a new range without clearing.
   * - A Drag operation updates the `lead` of the last range in this stack.
   */
  selections: SelectionRange[];

  /**
   * The single cell that currently owns the keyboard input.
   *
   * This is distinct from the selection range!
   * Example: If you select A1:B10, the range is huge, but `focusedCell`
   * might be at A1 (where you are typing). Pressing Enter moves `focusedCell`
   * to A2, but the range A1:B10 stays selected.
   */
  focusedCell: Nullable<GridPosition>;

  /**
   * Metadata about the last change.
   * Used to prevent feedback loops (e.g., if 'presence' updated the state,
   * do not broadcast that back to the websocket).
   */
  lastInteractionSource: Nullable<InteractionSource>;
}

/**
 * Special Id used in selections state to identify a select all range.
 */
export const SELECT_ALL_RANGE_ID = 'select-all';
